/* eslint-disable class-methods-use-this */
import axios from 'axios'
import * as StrapiTypes from './strapi.types'


enum PublicationState {
  preview = 'preview',
  live = 'live'
}

export default class StrapiService {
  readonly strapiUrl: string

  readonly publicationState: `${PublicationState}`

  constructor() {
    this.strapiUrl = process.env[ 'GATSBY_STRAPI_API_URL' ] as string
    this.publicationState = process.env[ 'GATSBY_ACTIVE_ENV' ] === 'prd' ? PublicationState.live : PublicationState.preview
  }

  private getDefaultQueryParams( publicationState: `${PublicationState}`, sort?: string ): string {
    const publicationStateParams = publicationState === 'preview'
      ? '_publicationState=preview&published_at_null=true'
      : '_publicationState=live'
    const sortParam = `&_sort=${sort ?? 'id:DESC'}`

    return `${publicationStateParams}${sortParam}`
  }

  private getUrlFilterContentIdParam = ( paramName: string ) => {
    const params = new URLSearchParams( window.location.search )
    const contentFilter = params.get( paramName )
    if ( !contentFilter ) {
      return ''
    }
    return `&_id=${contentFilter}`
  }

  private async getStrapiData<T>( url: string ): Promise<T> {
    const response = await axios.get<T>( encodeURI( `${this.strapiUrl}${url}` ) )
    return response.data
  }

  async getAllStrapiHomes(): Promise<StrapiTypes.StrapiHomes[]> {
    const homeContentParam = this.getUrlFilterContentIdParam( 'homes_content' )
    const forceGetProductionData = !homeContentParam
    const defaultQueryParams = this.getDefaultQueryParams( forceGetProductionData ? PublicationState.live : this.publicationState )
    const url = homeContentParam
      ? `/homes?_publicationState=${PublicationState.preview}${homeContentParam}`
      : `/homes?${defaultQueryParams}`
    const data = await this.getStrapiData<StrapiTypes.StrapiHomes[]>( url )
    return data
  }

  async getStrapiFaqsFilteredByDisplay( display: Partial<StrapiTypes.StrapiFaq['display']> ): Promise<StrapiTypes.StrapiFaq[]> {
    const data = await this.getStrapiData<StrapiTypes.StrapiFaq[]>( `/faqs?${this.getDefaultQueryParams( PublicationState.live )}` )
    const displayTuples = Object.entries( display )
    const filteredFaqs = data.filter(
      ( faq ) => displayTuples.every( ( [ key, value ] ) => faq.display[ key as keyof StrapiTypes.StrapiFaq['display'] ] === value )
    )

    return filteredFaqs
  }

  async getStrapiCheckoutDefinitions( productName?: string ): Promise<StrapiTypes.StrapiCheckoutDefinitions[]> {
    const baseUrl = `/checkout-definitions?_publicationState=${PublicationState.preview}&_sort=id:DESC`
    const url = productName ? `${baseUrl}&productName=${productName}` : baseUrl

    const definitions = await this.getStrapiData<StrapiTypes.StrapiCheckoutDefinitions[]>( url )

    const definitionsDict: Record<string, any> = {}
    const deduplicatedDefinitions = definitions.filter( ( definition ) => {
      if ( definitionsDict[ definition.productName ] ) {
        return false
      }

      definitionsDict[ definition.productName ] = true
      return true
    } )

    return deduplicatedDefinitions
  }
}
